import React, { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import styled from 'styled-components';
import sceneManifest from '../data/regions/goheung/scenes.json';
import locationData from '../data/regions/goheung/locations.json';
import regionMeta from '../data/regions/goheung/region.json';

// 좌측 내비게이션·krpano·미니맵이 같은 고흥군 컬러 원본을 사용한다.
// 브랜드 컬러 변경은 region.json 한 곳에서만 한다.
const GOHEUNG_THEME = regionMeta.theme;

// 배포 시에는 REACT_APP_MAP_TILE_URL로 안정적인 타일/CDN 주소를 주입할 수 있다.
// 기본값은 개발·검수용 OSM 공개 타일이며, 화면 안에 출처를 항상 표시한다.
const MAP_TILE_URL = process.env.REACT_APP_MAP_TILE_URL
  || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const MAP_ATTRIBUTION = process.env.REACT_APP_MAP_ATTRIBUTION
  || '© OpenStreetMap contributors';

const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: [MAP_TILE_URL],
      tileSize: 256,
      attribution: MAP_ATTRIBUTION,
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

const DEFAULT_CENTER = [127.39, 34.62];
const DEFAULT_ZOOM = 10.4;
const MIN_ZOOM = 10.2;
const EXPANDED_MIN_ZOOM = 9.7;
// 촬영 지점 전체를 포함하되, 사용자가 축소·이동해 고흥 권역 밖으로 벗어나지 않게 한다.
const GOHEUNG_MAX_BOUNDS = [[127.0, 34.38], [127.61, 34.82]];
const SCENE_SOURCE = 'goheung-scenes';
const REGION_SOURCE = 'goheung-region-hubs';
const DETAIL_PIN_MIN_ZOOM = 11.2;
// locations.json의 scenes가 실제 krpano XML에 생성되는 장면의 단일 기준이다.
// 과거에 제외한 촬영본은 scenes.json에 원본 이력으로 남아 있어도 지도에는 표시하지 않는다.
const ENABLED_SCENE_IDS = new Set(
  (locationData.locations || []).flatMap((location) => location.scenes || []),
);
const SCENES = (sceneManifest.scenes || []).filter((scene) => ENABLED_SCENE_IDS.has(scene.id));
const SCENE_BY_ID = new Map(SCENES.map((scene) => [scene.id, scene]));

// 자동 클러스터는 가까운 서로 다른 지역까지 합쳐 버릴 수 있다.
// 따라서 12개 제작 지역을 고정 허브로 만든다. 한 지역 안의 장면이 멀리 떨어진
// 경우도 있으므로(예: 1번 우주발사전망대·우주과학관·봉래산), 평균 좌표가 아닌
// 좌측 내비게이션에서 처음 여는 대표 장면의 좌표를 허브 위치로 사용한다.
const REGION_HUBS = (locationData.locations || []).map((location) => {
  const scenes = (location.scenes || [])
    .map((id) => SCENE_BY_ID.get(id))
    .filter((scene) => Number.isFinite(scene?.capture?.latitude) && Number.isFinite(scene?.capture?.longitude));
  if (!scenes.length) return null;

  const representativeScene = scenes[0];
  return {
    slug: location.slug,
    no: location.no,
    label: location.label,
    firstScene: representativeScene.id,
    sceneIds: scenes.map((scene) => scene.id),
    sceneCount: scenes.length,
    longitude: Number(representativeScene.capture.longitude),
    latitude: Number(representativeScene.capture.latitude),
  };
}).filter(Boolean);

const getSceneBounds = (scenes) => {
  const points = scenes
    .map((scene) => [Number(scene?.capture?.longitude), Number(scene?.capture?.latitude)])
    .filter(([longitude, latitude]) => Number.isFinite(longitude) && Number.isFinite(latitude));
  if (!points.length) return GOHEUNG_MAX_BOUNDS;

  const longitudes = points.map(([longitude]) => longitude);
  const latitudes = points.map(([, latitude]) => latitude);
  // 전체지도에서 가장자리 마커가 잘리지 않도록 촬영 좌표 외곽에 여유를 둔다.
  return [
    [Math.min(...longitudes) - 0.025, Math.min(...latitudes) - 0.02],
    [Math.max(...longitudes) + 0.025, Math.max(...latitudes) + 0.02],
  ];
};

const GOHEUNG_SCENE_BOUNDS = getSceneBounds(SCENES);

const toSceneFeatures = (scenes, activeSceneId) => ({
  type: 'FeatureCollection',
  features: scenes
    .filter((scene) => Number.isFinite(scene?.capture?.latitude) && Number.isFinite(scene?.capture?.longitude))
    .map((scene) => ({
      type: 'Feature',
      id: scene.id,
      geometry: {
        type: 'Point',
        coordinates: [scene.capture.longitude, scene.capture.latitude],
      },
      properties: {
        sceneId: scene.id,
        id: scene.id,
        location: scene.location,
        title: scene.title,
        active: scene.id === activeSceneId,
      },
    })),
});

const toRegionFeatures = (hubs, activeSceneId) => ({
  type: 'FeatureCollection',
  features: hubs.map((hub) => ({
    type: 'Feature',
    id: hub.slug,
    geometry: { type: 'Point', coordinates: [hub.longitude, hub.latitude] },
    properties: {
      slug: hub.slug,
      no: hub.no,
      label: hub.label,
      firstScene: hub.firstScene,
      sceneCount: hub.sceneCount,
      active: hub.sceneIds.includes(activeSceneId),
    },
  })),
});

const MapIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="M4 7.5 12 4l8 3.5L28 4v20.5L20 28l-8-3.5L4 28Z" />
    <path d="M12 4v20.5M20 7.5V28" />
  </svg>
);

const ExpandIcon = ({ collapse = false }) => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    {collapse ? (
      <>
        <path d="M13 4v9H4M19 28v-9h9M4 13l9-9M28 19l-9 9" />
      </>
    ) : (
      <>
        <path d="M12 4H4v8M20 28h8v-8M4 12l9-9M28 20l-9 9" />
      </>
    )}
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="m7 7 18 18M25 7 7 25" />
  </svg>
);

const GoheungMiniMap = ({ activeLocationSlug, activeSceneId, onNavigate }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const navigateRef = useRef(onNavigate);
  const expandedRef = useRef(false);
  const hoverPopupRef = useRef(null);
  const hoveredSceneIdRef = useRef(null);
  const hoveredRegionSlugRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [opened, setOpened] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  useEffect(() => {
    navigateRef.current = onNavigate;
  }, [onNavigate]);

  const scenes = SCENES;
  const sceneFeatures = useMemo(() => toSceneFeatures(scenes, activeSceneId), [activeSceneId, scenes]);
  const regionFeatures = useMemo(() => toRegionFeatures(REGION_HUBS, activeSceneId), [activeSceneId]);
  const activeScene = scenes.find((scene) => scene.id === activeSceneId) || null;

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return undefined;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: OSM_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      maxBounds: GOHEUNG_MAX_BOUNDS,
      attributionControl: true,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: true }), 'top-right');
    let regionNumberMarkers = [];

    // MapLibre 기본 title 툴팁은 브라우저마다 어두운 네이티브 스타일로 보인다.
    // krpano와 같은 밝은 유리 안내문을 쓰도록 버튼 이름을 data-tooltip로 옮긴다.
    const localizeNavigationControls = () => {
      const labels = {
        'maplibregl-ctrl-zoom-in': '확대',
        'maplibregl-ctrl-zoom-out': '축소',
        'maplibregl-ctrl-compass': '북쪽으로 회전',
      };
      Object.entries(labels).forEach(([className, label]) => {
        const button = mapContainerRef.current?.querySelector(`.${className}`);
        if (!button) return;
        button.setAttribute('aria-label', label);
        button.setAttribute('data-tooltip', label);
        button.removeAttribute('title');
      });
    };

    map.on('load', () => {
      localizeNavigationControls();
      // sceneId/slug를 promoteId로 고정해 setData() 이후에도 hover feature-state가 같은 핀을 가리킨다.
      map.addSource(SCENE_SOURCE, { type: 'geojson', promoteId: 'sceneId', data: toSceneFeatures(scenes, '') });
      map.addSource(REGION_SOURCE, { type: 'geojson', promoteId: 'slug', data: toRegionFeatures(REGION_HUBS, '') });
      map.addLayer({
        id: 'goheung-region-hubs-hover-halo',
        type: 'circle',
        source: REGION_SOURCE,
        maxzoom: DETAIL_PIN_MIN_ZOOM,
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 22, 0],
          'circle-color': GOHEUNG_THEME.accent,
          'circle-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.54, 0],
          'circle-blur': 0.24,
        },
      });
      map.addLayer({
        id: 'goheung-region-hubs',
        type: 'circle',
        source: REGION_SOURCE,
        maxzoom: DETAIL_PIN_MIN_ZOOM,
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 14, ['get', 'active'], 13, 10],
          'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], GOHEUNG_THEME.accent, ['get', 'active'], GOHEUNG_THEME.accent, GOHEUNG_THEME.main],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3, ['get', 'active'], 2.5, 1.5],
          'circle-opacity': 0.97,
        },
      });

      // Raster OSM 스타일에는 텍스트 glyph 서버가 없으므로, 지역 번호와 이름은
      // HTML 마커로 올린다. 전체지도에서는 01~12와 지역명을 함께 보이고,
      // 세부 장면 단계에서는 숨긴다.
      regionNumberMarkers = REGION_HUBS.map((hub) => {
        const element = document.createElement('span');
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const label = document.createElement('span');

        element.className = 'goheung-region-number-marker';
        element.setAttribute('aria-label', `${String(hub.no).padStart(2, '0')} ${hub.label} 지역 보기`);
        element.setAttribute('role', 'button');
        element.tabIndex = -1;
        element.dataset.slug = hub.slug;
        // MapLibre Marker는 요소 자체의 position:absolute와 transform으로 지도 좌표에
        // 고정한다. position을 덮어쓰면 DOM 흐름 위치만큼 모든 마커가 밀린다.
        element.style.cssText = 'display:grid;place-items:center;width:28px;height:28px;pointer-events:none;transition:opacity 120ms ease;';
        svg.setAttribute('viewBox', '0 0 28 28');
        svg.setAttribute('width', '28');
        svg.setAttribute('height', '28');
        text.setAttribute('x', '14');
        text.setAttribute('y', '14.5');
        text.setAttribute('fill', '#FFFFFF');
        text.setAttribute('font-size', '10');
        text.setAttribute('font-weight', '700');
        text.setAttribute('font-family', 'Arial, sans-serif');
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'middle');
        text.textContent = String(hub.no).padStart(2, '0');
        svg.appendChild(text);
        label.className = 'goheung-region-marker-label';
        label.textContent = `${String(hub.no).padStart(2, '0')} ${hub.label}`;
        element.appendChild(svg);
        element.appendChild(label);

        const focusRegion = () => {
          map.easeTo({
            center: [hub.longitude, hub.latitude],
            zoom: DETAIL_PIN_MIN_ZOOM + 0.55,
            duration: 420,
          });
        };
        element.addEventListener('mouseenter', () => {
          if (expandedRef.current) element.classList.add('is-hovered');
        });
        element.addEventListener('mouseleave', () => element.classList.remove('is-hovered'));
        element.addEventListener('click', (event) => {
          if (!expandedRef.current) return;
          event.stopPropagation();
          focusRegion();
        });
        element.addEventListener('keydown', (event) => {
          if (!expandedRef.current || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          focusRegion();
        });

        return new maplibregl.Marker({ element, anchor: 'center' })
          .setLngLat([hub.longitude, hub.latitude])
          .addTo(map);
      });

      const syncRegionNumberMarkers = () => {
        const visible = map.getZoom() < DETAIL_PIN_MIN_ZOOM;
        regionNumberMarkers.forEach((marker) => {
          const markerElement = marker.getElement();
          markerElement.classList.toggle('is-expanded-label', expandedRef.current);
          markerElement.tabIndex = expandedRef.current && visible ? 0 : -1;
          markerElement.style.pointerEvents = expandedRef.current && visible ? 'auto' : 'none';
          markerElement.style.opacity = visible ? '1' : '0';
          markerElement.style.visibility = visible ? 'visible' : 'hidden';
        });
      };
      map.on('zoom', syncRegionNumberMarkers);
      map.on('zoomend', syncRegionNumberMarkers);
      map.on('resize', syncRegionNumberMarkers);
      syncRegionNumberMarkers();
      map.addLayer({
        id: 'goheung-scene-points-hover-halo',
        type: 'circle',
        source: SCENE_SOURCE,
        minzoom: DETAIL_PIN_MIN_ZOOM,
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 19, 0],
          'circle-color': GOHEUNG_THEME.accent,
          'circle-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.68, 0],
          'circle-blur': 0.28,
        },
      });
      map.addLayer({
        id: 'goheung-scene-points',
        type: 'circle',
        source: SCENE_SOURCE,
        minzoom: DETAIL_PIN_MIN_ZOOM,
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 11, ['get', 'active'], 8, 5],
          'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], GOHEUNG_THEME.accent, ['get', 'active'], GOHEUNG_THEME.accent, GOHEUNG_THEME.main],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3.5, ['get', 'active'], 2, 1],
          'circle-opacity': 0.95,
        },
      });
      // 축소된 미니맵에서는 전체 44개 장면을 전부 그리지 않는다. 대신 현재 선택한
      // 지역의 장면만 항상 보여 준다. 1·8·12번처럼 촬영지가 멀리 떨어진 지역도
      // 같은 지역의 다른 시점을 놓치지 않도록 하기 위한 보조 레이어다.
      map.addLayer({
        id: 'goheung-active-location-scene-points',
        type: 'circle',
        source: SCENE_SOURCE,
        maxzoom: DETAIL_PIN_MIN_ZOOM,
        filter: ['==', ['get', 'location'], ''],
        paint: {
          'circle-radius': ['case', ['boolean', ['feature-state', 'hover'], false], 11, ['get', 'active'], 9, 7],
          'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], GOHEUNG_THEME.accent, ['get', 'active'], GOHEUNG_THEME.accent, GOHEUNG_THEME.main],
          'circle-stroke-color': '#FFFFFF',
          'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3, ['get', 'active'], 2.5, 1.5],
          'circle-opacity': 0.97,
        },
      });
      const showHoverPopup = (event, title, subtitle = '') => {
        if (!title) return;
        if (!hoverPopupRef.current) {
          hoverPopupRef.current = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            className: 'goheung-map-tooltip',
            offset: 12,
          });
        }
        hoverPopupRef.current
          .setLngLat(event.lngLat)
          .setText(subtitle ? `${title} · ${subtitle}` : title)
          .addTo(map);
      };

      map.on('click', 'goheung-region-hubs', (event) => {
        const feature = event.features?.[0];
        if (!feature?.geometry?.coordinates) return;
        map.easeTo({ center: feature.geometry.coordinates, zoom: DETAIL_PIN_MIN_ZOOM + 0.55, duration: 420 });
      });
      map.on('mouseenter', 'goheung-region-hubs', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mousemove', 'goheung-region-hubs', (event) => {
        const feature = event.features?.[0];
        const slug = feature?.properties?.slug;
        if (hoveredRegionSlugRef.current && hoveredRegionSlugRef.current !== slug) {
          map.setFeatureState({ source: REGION_SOURCE, id: hoveredRegionSlugRef.current }, { hover: false });
        }
        if (!slug) return;
        hoveredRegionSlugRef.current = slug;
        map.setFeatureState({ source: REGION_SOURCE, id: slug }, { hover: true });
        showHoverPopup(event, feature.properties?.label, `파노라마 ${feature.properties?.sceneCount || 0}개`);
      });
      map.on('mouseleave', 'goheung-region-hubs', () => {
        map.getCanvas().style.cursor = '';
        if (hoveredRegionSlugRef.current) {
          map.setFeatureState({ source: REGION_SOURCE, id: hoveredRegionSlugRef.current }, { hover: false });
          hoveredRegionSlugRef.current = null;
        }
        hoverPopupRef.current?.remove();
      });

      ['goheung-scene-points', 'goheung-active-location-scene-points'].forEach((layerId) => {
        map.on('click', layerId, (event) => {
          const feature = event.features?.[0];
          const location = feature?.properties?.location;
          const scene = feature?.properties?.id;
          const startView = SCENE_BY_ID.get(scene)?.startView || null;
          if (location && scene) navigateRef.current?.(location, scene, startView);
        });
        map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mousemove', layerId, (event) => {
          const feature = event.features?.[0];
          const hoveredId = feature?.properties?.sceneId;
          if (hoveredSceneIdRef.current && hoveredSceneIdRef.current !== hoveredId) {
            map.setFeatureState({ source: SCENE_SOURCE, id: hoveredSceneIdRef.current }, { hover: false });
          }
          if (hoveredId) {
            hoveredSceneIdRef.current = hoveredId;
            map.setFeatureState({ source: SCENE_SOURCE, id: hoveredId }, { hover: true });
          }
          if (!feature || feature.properties?.active === 'true' || feature.properties?.active === true) {
            hoverPopupRef.current?.remove();
            return;
          }
          showHoverPopup(event, feature.properties?.title);
        });
        map.on('mouseleave', layerId, () => {
          map.getCanvas().style.cursor = '';
          if (hoveredSceneIdRef.current) {
            map.setFeatureState({ source: SCENE_SOURCE, id: hoveredSceneIdRef.current }, { hover: false });
            hoveredSceneIdRef.current = null;
          }
          hoverPopupRef.current?.remove();
        });
      });
      setMapReady(true);
    });

    mapRef.current = map;
    return () => {
      hoverPopupRef.current?.remove();
      hoverPopupRef.current = null;
      hoveredSceneIdRef.current = null;
      hoveredRegionSlugRef.current = null;
      regionNumberMarkers.forEach((marker) => marker.remove());
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [scenes]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const source = map.getSource(SCENE_SOURCE);
    if (source) source.setData(sceneFeatures);
  }, [mapReady, sceneFeatures]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map) return;
    const source = map.getSource(REGION_SOURCE);
    if (source) source.setData(regionFeatures);

    // 전체지도 저줌에서는 점·원형 번호 대신 텍스트 라벨만 사용한다.
    // 지도 확대한 뒤에는 세부 촬영지 핀이 보이므로 지역 허브 원형은 계속 숨긴다.
    ['goheung-region-hubs-hover-halo', 'goheung-region-hubs'].forEach((layerId) => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', expanded ? 'none' : 'visible');
      }
    });

    const activeHub = REGION_HUBS.find((hub) => hub.sceneIds.includes(activeSceneId));
    mapContainerRef.current?.querySelectorAll('.goheung-region-number-marker').forEach((marker) => {
      marker.classList.toggle('is-active', marker.dataset.slug === activeHub?.slug);
    });
  }, [activeSceneId, expanded, mapReady, regionFeatures]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !map.getLayer('goheung-active-location-scene-points')) return;
    map.setFilter('goheung-active-location-scene-points', [
      '==',
      ['get', 'location'],
      activeLocationSlug || '__no_active_location__',
    ]);
    map.setLayoutProperty(
      'goheung-active-location-scene-points',
      'visibility',
      expanded ? 'none' : 'visible'
    );
  }, [activeLocationSlug, expanded, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || expanded || !activeScene?.capture) return;
    // 좌측 지역 내비게이션은 각 지역의 첫 파노라마를 시작 장면으로 연다.
    // 미니맵도 지역 범위 평균이 아닌, 현재 선택된 그 장면의 실제 GPS를 중심으로 둔다.
    map.easeTo({
      center: [activeScene.capture.longitude, activeScene.capture.latitude],
      duration: 450,
    });
  }, [activeScene, expanded, mapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapReady || !map || !opened) return undefined;

    const syncExpandedViewport = () => {
      map.resize();
      map.setMinZoom(expanded ? EXPANDED_MIN_ZOOM : MIN_ZOOM);
      const regionMarkers = mapContainerRef.current?.querySelectorAll('.goheung-region-number-marker') || [];
      const labelsVisible = map.getZoom() < DETAIL_PIN_MIN_ZOOM;
      regionMarkers.forEach((marker) => {
        marker.classList.toggle('is-expanded-label', expanded);
        marker.tabIndex = expanded && labelsVisible ? 0 : -1;
        marker.style.pointerEvents = expanded && labelsVisible ? 'auto' : 'none';
      });
      ['goheung-region-hubs-hover-halo', 'goheung-region-hubs'].forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', expanded ? 'none' : 'visible');
        }
      });
      if (expanded) {
        map.fitBounds(GOHEUNG_SCENE_BOUNDS, {
          padding: { top: 72, right: 64, bottom: 56, left: 64 },
          // 전체보기는 언제나 12개 지역 허브 단계에서 시작한다.
          // 세부 장면 핀은 사용자가 확대해야 나타난다.
          maxZoom: DETAIL_PIN_MIN_ZOOM - 0.25,
          duration: 0,
        });
      }
    };
    const frame = window.requestAnimationFrame(syncExpandedViewport);
    // 중앙 창으로 커지는 CSS 전환이 끝난 뒤 한 번 더 계산해 경계 마커가 잘리지 않게 한다.
    const afterTransition = window.setTimeout(syncExpandedViewport, expanded ? 280 : 0);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(afterTransition);
    };
  }, [expanded, mapReady, opened]);

  const closeMap = () => {
    setExpanded(false);
    setOpened(false);
  };

  return (
    <>
      {expanded && <MapBackdrop aria-hidden="true" onClick={() => setExpanded(false)} />}
      {!opened && (
        <MapOpenButton
          type="button"
          aria-label="미니맵 열기"
          data-tooltip="미니맵"
          onClick={() => setOpened(true)}
        >
          <MapIcon />
        </MapOpenButton>
      )}
      <MapShell $expanded={expanded} $opened={opened} aria-label="고흥 파노라마 지도">
        <MapCanvas ref={mapContainerRef} />
        <MapOverlay>
          {activeScene && (
            <MapLocationBadge title={activeScene.title}>
              <ActiveDot aria-hidden="true" />
              <MapSceneTitle>{activeScene.title}</MapSceneTitle>
            </MapLocationBadge>
          )}
          <MapControlGroup>
            <MapActionButton
              type="button"
              aria-label={expanded ? '미니맵으로 돌아가기' : '전체지도 보기'}
              aria-expanded={expanded}
              data-tooltip={expanded ? '미니맵으로 돌아가기' : '전체지도'}
              onClick={() => setExpanded((value) => !value)}
            >
              <ExpandIcon collapse={expanded} />
            </MapActionButton>
            <MapActionButton type="button" aria-label="지도 접기" data-tooltip="지도 접기" onClick={closeMap}>
              <CloseIcon />
            </MapActionButton>
          </MapControlGroup>
        </MapOverlay>
      </MapShell>
    </>
  );
};

export default GoheungMiniMap;

const MapShell = styled.section`
  position: fixed;
  top: ${({ $expanded }) => ($expanded ? '50%' : 'auto')};
  right: ${({ $expanded }) => ($expanded ? 'auto' : '18px')};
  // 하단 krpano 조작 바가 컴팩트해진 뒤에는 지도와 수평으로 겹치지 않는다.
  // 접힌 지도는 화면 모서리에 가깝게 두어 하단 UI와 하나의 영역으로 보이게 한다.
  bottom: ${({ $expanded }) => ($expanded ? 'auto' : '24px')};
  left: ${({ $expanded }) => ($expanded ? '50%' : 'auto')};
  z-index: 1200;
  // 전체지도는 별도 창에서 충분히 크게 제공하므로, 기본 지도는
  // 장면 조작을 방해하지 않는 보조 크기로 유지한다.
  width: ${({ $expanded }) => ($expanded ? 'min(900px, calc(100vw - 48px))' : 'clamp(228px, 14vw, 264px)')};
  height: ${({ $expanded }) => ($expanded ? 'min(680px, calc(100vh - 72px))' : 'clamp(148px, 14vh, 166px)')};
  overflow: hidden;
  color: #ffffff;
  background: rgba(7, 30, 45, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: ${({ $expanded }) => ($expanded ? '16px' : '12px')};
  box-shadow: none;
  opacity: ${({ $opened }) => ($opened ? 1 : 0)};
  visibility: ${({ $opened }) => ($opened ? 'visible' : 'hidden')};
  pointer-events: ${({ $opened }) => ($opened ? 'auto' : 'none')};
  transform: ${({ $expanded, $opened }) => {
    if ($expanded) return 'translate(-50%, -50%)';
    return $opened ? 'translateY(0)' : 'translateY(10px)';
  }};
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;

  @media (max-width: 700px) {
    top: ${({ $expanded }) => ($expanded ? '50%' : 'auto')};
    right: ${({ $expanded }) => ($expanded ? 'auto' : '10px')};
    bottom: ${({ $expanded }) => ($expanded ? 'auto' : '10px')};
    left: ${({ $expanded }) => ($expanded ? '50%' : 'auto')};
    width: ${({ $expanded }) => ($expanded ? 'calc(100vw - 20px)' : 'min(280px, calc(100vw - 20px))')};
    height: ${({ $expanded }) => ($expanded ? 'calc(100vh - 20px)' : '176px')};
  }
`;

const MapBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1199;
  background: rgba(5, 12, 24, 0.58);
  backdrop-filter: blur(3px);
`;

const MapOpenButton = styled.button`
  position: fixed;
  right: 24px;
  bottom: 22px;
  z-index: 1200;
  display: grid;
  place-items: center;
  width: 46px;
  height: 42px;
  padding: 0;
  color: #ffffff;
  background: rgba(7, 30, 45, 0.28);
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.95);
  filter: none;
  cursor: pointer;

  &::after {
    position: absolute;
    right: 0;
    bottom: calc(100% + 7px);
    padding: 5px 8px;
    color: #17213A;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.62);
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    content: attr(data-tooltip);
    font-size: 11px;
    font-weight: 700;
    opacity: 0;
    pointer-events: none;
    transform: translateY(3px);
    transition: opacity 0.12s ease, transform 0.12s ease;
    white-space: nowrap;
  }

  svg {
    width: 30px;
    height: 30px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover,
  &:focus-visible {
    color: ${GOHEUNG_THEME.accent};
    border-bottom-color: ${GOHEUNG_THEME.accent};
    outline: none;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MapOverlay = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 7px;
  pointer-events: none;
`;

const MapControlGroup = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 3px;
  pointer-events: auto;
`;

const MapLocationBadge = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: calc(100% - 76px);
  gap: 7px;
  padding: 6px 8px;
  background: rgba(7, 30, 45, 0.64);
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.55);
`;

const ActiveDot = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  background: ${GOHEUNG_THEME.accent};
  border: 1px solid rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(240, 79, 37, 0.3);
`;

const MapSceneTitle = styled.span`
  overflow: hidden;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MapActionButton = styled.button`
  position: relative;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  color: #ffffff;
  background: rgba(7, 30, 45, 0.42);
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 0;
  filter: none;
  cursor: pointer;
  pointer-events: auto;

  &::after {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    padding: 5px 7px;
    color: #17213A;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.62);
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    content: attr(data-tooltip);
    font-size: 10px;
    font-weight: 600;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-3px);
    transition: opacity 0.12s ease, transform 0.12s ease;
    white-space: nowrap;
  }

  svg {
    width: 23px;
    height: 23px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.6;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover,
  &:focus-visible {
    color: ${GOHEUNG_THEME.accent};
    border-bottom-color: ${GOHEUNG_THEME.accent};
    background: rgba(7, 30, 45, 0.72);
    outline: none;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MapCanvas = styled.div`
  width: 100%;
  height: 100%;
  background: #dbe5ec;
  border-radius: inherit;

  .maplibregl-canvas,
  .maplibregl-map {
    border-radius: inherit;
  }

  .maplibregl-ctrl-top-right {
    top: 44px;
    right: 5px;
  }

  .maplibregl-ctrl-group button {
    position: relative;
    overflow: visible;
    width: 26px;
    height: 26px;
  }

  .maplibregl-ctrl-group button::after {
    position: absolute;
    top: 50%;
    right: calc(100% + 7px);
    padding: 5px 8px;
    color: #17213A;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.62);
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    content: attr(data-tooltip);
    font-family: inherit;
    font-size: 10px;
    font-weight: 700;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-50%) translateX(3px);
    transition: opacity 0.12s ease, transform 0.12s ease;
    white-space: nowrap;
  }

  .maplibregl-ctrl-group button:hover::after,
  .maplibregl-ctrl-group button:focus-visible::after {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }

  .maplibregl-ctrl-attrib {
    background: rgba(255, 255, 255, 0.72);
    font-size: 8px;
  }

  .goheung-region-number-marker .goheung-region-marker-label {
    position: absolute;
    top: 50%;
    left: calc(100% + 7px);
    display: block;
    width: max-content;
    max-width: 170px;
    overflow: hidden;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.04em;
    opacity: 0;
    text-align: left;
    text-overflow: ellipsis;
    text-shadow: 0 1px 3px rgba(8, 21, 35, 0.82);
    transform: translateY(-50%) translateX(-4px);
    transition: opacity 0.16s ease, transform 0.16s ease;
    white-space: nowrap;
  }

  .goheung-region-number-marker.is-expanded-label .goheung-region-marker-label {
    top: 50%;
    left: 50%;
    max-width: 190px;
    padding: 6px 9px;
    color: ${GOHEUNG_THEME.onMain};
    background: ${GOHEUNG_THEME.main}e0;
    border: 1px solid rgba(255, 255, 255, 0.76);
    border-radius: 5px;
    box-shadow: 0 2px 9px rgba(5, 20, 34, 0.24);
    opacity: 1;
    transform: translate(-50%, -50%);
  }

  .goheung-region-number-marker.is-expanded-label {
    cursor: pointer;
  }

  .goheung-region-number-marker.is-expanded-label svg {
    opacity: 0;
  }

  .goheung-region-number-marker.is-expanded-label.is-active .goheung-region-marker-label {
    background: ${GOHEUNG_THEME.accent};
    border-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 0 0 3px rgba(240, 79, 37, 0.2), 0 3px 10px rgba(5, 20, 34, 0.28);
  }

  .goheung-region-number-marker.is-expanded-label.is-hovered .goheung-region-marker-label,
  .goheung-region-number-marker.is-expanded-label:focus-visible .goheung-region-marker-label {
    color: ${GOHEUNG_THEME.onMain};
    background: ${GOHEUNG_THEME.accent};
    border-color: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 3px rgba(240, 79, 37, 0.3), 0 4px 13px rgba(5, 20, 34, 0.32);
    outline: none;
    transform: translate(-50%, -50%) scale(1.05);
  }

  .goheung-map-tooltip {
    pointer-events: none;
  }

  .goheung-map-tooltip .maplibregl-popup-content {
    padding: 6px 8px;
    color: #17213A;
    background: rgba(255, 255, 255, 0.84);
    border: 1px solid rgba(255, 255, 255, 0.62);
    border-radius: 3px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;
  }

  .goheung-map-tooltip .maplibregl-popup-tip {
    border-top-color: rgba(255, 255, 255, 0.84);
    border-bottom-color: rgba(255, 255, 255, 0.84);
  }
`;
